// ─── Tickets de helpdesk ─────────────────────────────────────────────────────
// El ciclo de casos del backend ahora lleva TICKET: `conversation.ticket_number`
// es el del caso VIGENTE y `case_history[].ticket_number` el de cada caso ya
// sellado. TODO campo es opcional (el despliegue del backend va en paralelo),
// así que cada helper degrada a la etiqueta por número de caso ("Caso #N") que
// la app ya usaba.
//
// Vive fuera de los componentes a propósito: son funciones puras testeables sin
// montar el hilo (que puede traer miles de mensajes).

import dayjs from "dayjs";

// ── Etiquetas ───────────────────────────────────────────────────────────────

// Referencia al ticket. Sin número (legacy) no hay nada que etiquetar: null.
export function ticketLabel(ticketNumber, t) {
  if (ticketNumber == null || ticketNumber === "") return null;
  return t("Ticket #{{t}}").replace("{{t}}", ticketNumber);
}

// Referencia corta a un caso: "Ticket #12" si se conoce el ticket, si no
// "Caso #3" (el texto que la app usaba antes de los tickets).
export function caseLabel(caseNumber, ticketNumber, t) {
  return ticketLabel(ticketNumber, t) ?? `${t("Caso")} #${caseNumber ?? 1}`;
}

// Separador de caso del hilo: la fecha viaja acá porque cuando hay corte de
// caso se suprime el separador de día. Año solo si no es el año en curso.
export function caseSeparatorLabel(caseNumber, ticketNumber, date, t) {
  const d = dayjs(date);
  const fecha = d.isSame(dayjs(), "year") ? d.format("D MMM") : d.format("D MMM YYYY");
  return `${caseLabel(caseNumber, ticketNumber, t)} · ${fecha}`;
}

// Pill de reapertura (el contacto volvió a escribir tras un resolve).
export function caseReopenLabel(caseNumber, ticketNumber, t) {
  const n = caseNumber ?? 1;
  return ticketLabel(ticketNumber, t)
    ? t("Ticket #{{t}} abierto — el contacto volvió a escribir").replace("{{t}}", ticketNumber)
    : t("Caso #{{n}} abierto — el contacto volvió a escribir").replace("{{n}}", n);
}

// Divisor de fin de caso (mensaje de sistema `kind: 'case_closed'`).
// Las claves son la frase COMPLETA (con placeholders): partir el texto en
// fragmentos se lleva mal con los idiomas que reordenan la oración.
// `bulk_cleanup` se queda SIEMPRE en "Caso #N": es un cierre general de casos
// antiguos, donde el número de caso es el referente que el agente reconoce.
export function caseClosedLabel({ caseNumber, ticketNumber, resolvedByName, origin, t }) {
  const n = caseNumber ?? 1;
  const hasTicket = ticketNumber != null && ticketNumber !== "";

  if (origin === "bulk_cleanup") {
    return t("Caso #{{n}} cerrado — cierre general de casos antiguos").replace("{{n}}", n);
  }
  if (origin === "auto_idle") {
    return hasTicket
      ? t("Ticket #{{t}} cerrado automáticamente por inactividad").replace("{{t}}", ticketNumber)
      : t("Caso #{{n}} cerrado automáticamente por inactividad").replace("{{n}}", n);
  }
  if (hasTicket) {
    return resolvedByName
      ? t("Ticket #{{t}} cerrado por {{nombre}}")
          .replace("{{t}}", ticketNumber)
          .replace("{{nombre}}", resolvedByName)
      : t("Ticket #{{t}} cerrado").replace("{{t}}", ticketNumber);
  }
  return resolvedByName
    ? t("Caso #{{n}} cerrado por {{nombre}}")
        .replace("{{n}}", n)
        .replace("{{nombre}}", resolvedByName)
    : t("Caso #{{n}} cerrado").replace("{{n}}", n);
}

// Nombre del agente que cerró el caso. Si `getDisplayName` no resuelve (devuelve
// el id crudo) o el cierre lo hizo el sistema, no hay nombre que mostrar.
export function closedByName(resolvedBy, getDisplayName) {
  if (!resolvedBy || resolvedBy === "system") return null;
  const name = getDisplayName ? getDisplayName(resolvedBy) : null;
  if (!name || name === resolvedBy) return null;
  return name;
}

// Etiqueta del origen de un cierre, o null si el backend no lo mandó (o no es
// uno de los conocidos: `api_outbound` no se etiqueta, ya lo dice el texto).
const ORIGIN_LABELS = {
  bulk_cleanup: "Cierre general",
  auto_idle: "Cierre automático",
};

export function originLabel(origin, t) {
  return ORIGIN_LABELS[origin] ? t(ORIGIN_LABELS[origin]) : null;
}

// ── Índices caso ↔ ticket ───────────────────────────────────────────────────

// Número de caso → entrada sellada de `case_history`.
export function caseEntriesByNumber(conversation) {
  const map = new Map();
  const history = Array.isArray(conversation?.case_history) ? conversation.case_history : [];
  for (const entry of history) {
    if (entry && entry.number != null) map.set(entry.number, entry);
  }
  return map;
}

// Mapa caso → ticket. Los casos sellados lo traen en su propia entrada; el
// VIGENTE solo en `conversation.ticket_number` (todavía no está en el historial).
export function ticketByCase(conversation) {
  const map = new Map();
  for (const [number, entry] of caseEntriesByNumber(conversation)) {
    if (entry.ticket_number != null) map.set(number, entry.ticket_number);
  }
  if (conversation?.ticket_number != null) {
    map.set(conversation.case_number || 1, conversation.ticket_number);
  }
  return map;
}

// Ticket de un caso concreto, o null si no se conoce (caso legacy).
export function ticketForCase(conversation, caseNumber) {
  if (caseNumber == null) return null;
  return ticketByCase(conversation).get(caseNumber) ?? null;
}

// Ambos índices en un solo objeto ESTABLE por conversación: el hilo memoizado
// recibe una sola prop y React.memo no se rompe en cada render del detalle.
export function caseIndexFor(conversation) {
  return {
    tickets: ticketByCase(conversation),
    entries: caseEntriesByNumber(conversation),
  };
}

// Huella del contenido que alimenta `caseIndexFor`. El objeto `conversation`
// cambia de identidad con CUALQUIER evento (asignación, resolve, read), pero el
// ciclo de casos cambia muy de vez en cuando. Con esta clave por contenido, el
// índice conserva identidad entre esos eventos y el hilo memoizado no se
// re-renderiza entero (≈200 ms en un hilo de miles de mensajes).
export function caseIndexKey(conversation) {
  if (!conversation) return "";
  const parts = [`${conversation.case_number || 1}:${conversation.ticket_number ?? ""}`];
  for (const [number, entry] of caseEntriesByNumber(conversation)) {
    parts.push(
      [
        number,
        entry.ticket_number ?? "",
        entry.resolved_by ?? "",
        entry.origin ?? "",
        entry.note ?? "",
      ].join(":"),
    );
  }
  return parts.join("|");
}

// ── Búsqueda ────────────────────────────────────────────────────────────────

// ¿La consulta ES un ticket? Formas: "#123", "t123", "T-123", "ticket 123",
// "ticket#123". Los dígitos SOLOS no cuentan: la búsqueda por teléfono usa esa
// misma forma ("56912345678") y no debe convertirse en un lookup de ticket.
const TICKET_QUERY_RE = /^(?:#|t|ticket)\s*[-#]?\s*(\d{1,9})$/i;

export function parseTicketQuery(query) {
  if (typeof query !== "string") return null;
  const match = query.trim().match(TICKET_QUERY_RE);
  if (!match) return null;
  const number = Number(match[1]);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}
